import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { UserButton } from '@clerk/react'
import { HelpCircle, Search, Upload, X, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Logo } from '@/components/ui/Logo'
import { ActiveTasksBanner, TaskBar } from '@/components/tasks'
import { useCredits } from '@/hooks/useCredits'
import { useTaskCompletionInvalidation } from '@/hooks/useTaskCompletionInvalidation'
import { ThemePanelTrigger } from '@/components/theme/ThemePanel'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { SupportDialog } from '@/components/dialogs/SupportDialog'
import { openCookieSettings } from '@/lib/consent'
import { SEARCH_DEBOUNCE_MS } from '@/lib/constants'
import { getMarketingSiteUrl } from '@/lib/site'

const navItems = [
  { to: '/', label: 'Dashboard' },
  { to: '/designs', label: 'Designs' },
  { to: '/batches', label: 'Batches' }
]

const MARKETING_SITE_URL = getMarketingSiteUrl()

const AppLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: credits, loading: creditsLoading } = useCredits()
  const [supportOpen, setSupportOpen] = useState(false)

  // Auto-invalidate caches when tasks complete
  useTaskCompletionInvalidation()

  // Search input state
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchValue, setSearchValue] = useState('')
  const debounceTimerRef = useRef<number | null>(null)

  // Sync search input with URL param when on designs page
  useEffect(() => {
    if (location.pathname === '/designs') {
      const urlQuery = searchParams.get('q') || ''
      setSearchValue(urlQuery)
    }
  }, [location.pathname, searchParams])

  // Handle search with debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)

    // Clear existing timer
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current)
    }

    // Debounce navigation (300ms)
    debounceTimerRef.current = window.setTimeout(() => {
      if (value.length >= 2) {
        navigate(`/designs?q=${encodeURIComponent(value)}`)
      } else if (value.length === 0 && location.pathname === '/designs') {
        // Clear search when input is emptied
        navigate('/designs')
      }
    }, SEARCH_DEBOUNCE_MS)
  }, [navigate, location.pathname])

  // Handle Enter key for immediate search
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Clear debounce timer
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current)
      }
      if (searchValue.length >= 2) {
        navigate(`/designs?q=${encodeURIComponent(searchValue)}`)
      }
    } else if (e.key === 'Escape') {
      searchInputRef.current?.blur()
    }
  }, [searchValue, navigate])

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    if (location.pathname === '/designs') {
      navigate('/designs')
    }
    searchInputRef.current?.focus()
  }, [navigate, location.pathname])

  // Keyboard shortcut: Cmd/Ctrl+K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
        searchInputRef.current?.select()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const creditLabel = creditsLoading ? '—' : credits ? `${credits.total_balance}` : '—'

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground"
              data-ph-event="navigation_clicked"
              data-ph-component="header"
              data-ph-element="logo"
              data-ph-dest-type="internal_page"
              data-ph-dest="/"
            >
              <Logo size={36} className="h-9 w-9" />
              MerchMuse
            </Link>
            <nav className="hidden items-center gap-1 md:flex">
              {navItems.map((item) => {
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition hover:bg-muted hover:text-foreground ${
                      active ? 'bg-muted text-foreground' : 'text-muted-foreground'
                    }`}
                    data-ph-event="navigation_clicked"
                    data-ph-component="header_nav"
                    data-ph-element={item.label.toLowerCase().replace(/\s+/g, '_')}
                    data-ph-dest-type="internal_page"
                    data-ph-dest={item.to}
                  >
                    {item.label}
                  </NavLink>
                )
              })}
            </nav>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            <div className="relative hidden w-full max-w-[320px] md:block">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                className="w-full bg-background pl-9 pr-16"
                placeholder="Search designs..."
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {searchValue ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <span className="pointer-events-none absolute right-2.5 top-2 rounded border px-1 text-[11px] text-muted-foreground">
                  ⌘K
                </span>
              )}
            </div>

            <Button
              onClick={() => navigate('/designs/upload?source=header')}
              size="sm"
              className="hidden sm:inline-flex"
              data-ph-event="cta_clicked"
              data-ph-component="header"
              data-ph-element="upload"
              data-ph-dest-type="internal_page"
              data-ph-dest="/designs/upload?source=header"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </Button>

            <TaskBar />

            <Button
              variant="outline"
              size="sm"
              className="hidden items-center gap-1.5 rounded-[var(--radius)] border border-muted bg-muted/60 px-3 sm:inline-flex"
              onClick={() => navigate('/billing')}
              data-ph-event="cta_clicked"
              data-ph-component="header"
              data-ph-element="credit_balance"
              data-ph-dest-type="internal_page"
              data-ph-dest="/billing"
            >
              <Zap className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />
              <span className="font-medium">{creditLabel}</span>
            </Button>

            <ThemeToggle />

            <UserButton
              appearance={{
                elements: {
                  avatarBox: 'h-9 w-9'
                }
              }}
            >
              <UserButton.MenuItems>
                <UserButton.Action
                  label="Contact Support"
                  labelIcon={<HelpCircle className="h-4 w-4" />}
                  onClick={() => setSupportOpen(true)}
                />
              </UserButton.MenuItems>
            </UserButton>
          </div>
        </div>
      </header>

      {/* Adaptive banner for active tasks when navigating away from entity pages */}
      <ActiveTasksBanner />

      <main className="mx-auto w-full max-w-6xl px-4 pb-12 pt-6">
        <Outlet />
      </main>

      <footer className="border-t bg-card/80 py-6">
        <div className="mx-auto w-full max-w-6xl px-4 text-center text-xs text-muted-foreground">
          <p>MerchMuse is not affiliated with, endorsed by, or sponsored by Amazon. "Merch by Amazon" is a trademark of Amazon.com, Inc.</p>
          <p className="mt-2">
            Need help? <a href="mailto:support@merchmuse.art" className="underline hover:text-foreground">support@merchmuse.art</a>
          </p>
          <p className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <a
              href={`${MARKETING_SITE_URL}/privacy/`}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-foreground"
              data-ph-event="navigation_clicked"
              data-ph-component="footer"
              data-ph-element="privacy_policy"
              data-ph-dest-type="external_page"
              data-ph-dest={`${MARKETING_SITE_URL}/privacy/`}
            >
              Privacy Policy
            </a>
            <button
              type="button"
              onClick={openCookieSettings}
              className="underline hover:text-foreground"
              data-ph-event="cta_clicked"
              data-ph-component="footer"
              data-ph-element="cookie_settings"
            >
              Cookie Settings
            </button>
          </p>
        </div>
      </footer>

      <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />

      {/* Theme Settings Panel - Dev + staging (full customization) */}
      {(import.meta.env.DEV || import.meta.env.VITE_ENABLE_THEME_PANEL === 'true') && <ThemePanelTrigger />}
    </div>
  )
}

export default AppLayout
